import { Skeleton, Stack, Box } from '@mui/material';
import { AutomationDialogContent } from './AutomationDialogContent';
import { AutomationFormVariant } from '../../types/enums/AutomationFormVariant';
import {
  ConfigurationCanvasLeftPanel,
  ConfigurationCanvasPanelContent,
  ConfigurationCanvasPanelHeader,
} from '../pages/configuration-canvas-panel';
import { automationsSortOptions } from './consts';
import { WithEnvConnectionErrorPanel } from '../pages/canvas-pages/united-canvas/canvas-page/error-panel/EnvConnectionErrorPanel';
import { getPaywallSourceFromAutomationVariant } from '../dashboard/paywall/sales-activated-trial/helper';
import PaywallVerticalLayout from '../dashboard/paywall/sales-activated-trial/PaywallVerticalLayout';
import usePaywall from '../dashboard/paywall/usePaywall';
import { SaveOrCreateAutomationParams } from './useAutomationApiPerType';
import { useGetSidePadding } from '../pages/configuration-canvas-panel/useGetSidePadding';
import {
  PanelEnvSelector,
  panelEnvSelectorHack,
} from '../pages/environments/connect-env/PanelEnvSelector';
import { useGetAutomationFilters } from './filters/helpers';

interface AutomationDialogProps {
  onSaveOrCreate: (params: SaveOrCreateAutomationParams) => any;
  onToggleActivation: (
    automationJson: AutomationStructureNew,
    deployToOrgIds: string[],
    isActive: boolean,
  ) => any;
  onDeleteAutomation: (automationId: string, automationType: AutomationType) => any;
  automations: AutomationStructureNew[];
  crmOrgId: string;
  isLoading?: boolean;
  automationVariant: AutomationFormVariant;
  onDeployAutomation: (
    deployAutomationDto: AutomationStructureNew,
    deployToOrgIds: string[],
  ) => any;
  onClose?: () => void;
  disableCanvasTemplates?: boolean;
}

export const AutomationDialog = ({
  crmOrgId,
  automations,
  onSaveOrCreate,
  isLoading,
  onToggleActivation,
  onDeleteAutomation,
  automationVariant,
  onDeployAutomation,
  onClose,
  disableCanvasTemplates,
}: AutomationDialogProps) => {
  const { isPaywallActive } = usePaywall();
  const paywallSource = getPaywallSourceFromAutomationVariant(automationVariant);
  const sidePadding = useGetSidePadding();

  const filters = useGetAutomationFilters({
    automations: automations ?? [],
    crmOrgId: crmOrgId,
  });

  const headerContent = (
    <ConfigurationCanvasPanelHeader onClose={onClose} withExpandedModeButton={!isPaywallActive}>
      {automationVariant === AutomationFormVariant.PLAYBOOK_ALERT ? 'Alerts' : 'Automations'}
    </ConfigurationCanvasPanelHeader>
  );

  if (isLoading) {
    return (
      <ConfigurationCanvasLeftPanel>
        <WithEnvConnectionErrorPanel shouldWaitForInitialFetch={false}>
          {headerContent}
          <ConfigurationCanvasPanelContent>
            <Stack spacing={2} direction="column">
              <Skeleton variant="text" width={'80%'} height={46} />
              <Skeleton variant="rectangular" width={296} height={42} />
              <Skeleton variant="rectangular" animation="wave" width={'100%'} height={100} />
              <Skeleton variant="rectangular" animation="wave" width={'100%'} height={100} />
              <Skeleton variant="rectangular" animation="wave" width={'100%'} height={100} />
              <Skeleton variant="rectangular" animation="wave" width={'100%'} height={100} />
            </Stack>
          </ConfigurationCanvasPanelContent>
        </WithEnvConnectionErrorPanel>
      </ConfigurationCanvasLeftPanel>
    );
  }

  return (
    <ConfigurationCanvasLeftPanel
      sx={{
        '.configuration-canvas-panel-content': {
          overflow: 'hidden',
        },
        ...panelEnvSelectorHack,
      }}
      withExpandedModeButton={!isPaywallActive}
    >
      <WithEnvConnectionErrorPanel shouldWaitForInitialFetch={false}>
        <PanelEnvSelector filterByEnvsUsedInCanvas />
        {headerContent}
        <ConfigurationCanvasPanelContent
          sx={{
            padding: 0, //needed in order to override the existing value
          }}
        >
          {isPaywallActive && (
            <Box
              sx={{
                padding: `0 ${sidePadding}px 16px`,
                height: '100%',
              }}
            >
              <PaywallVerticalLayout paywallSource={paywallSource} variant="regular" />
            </Box>
          )}
          {!isPaywallActive && (
            <AutomationDialogContent
              onSaveOrCreate={onSaveOrCreate}
              onToggleActivation={onToggleActivation}
              onDeleteAutomation={onDeleteAutomation}
              automations={automations}
              crmOrgId={crmOrgId}
              sortOptions={automationsSortOptions}
              onDeployAutomation={onDeployAutomation}
              disableCanvasTemplates={disableCanvasTemplates}
              filters={filters}
            />
          )}
        </ConfigurationCanvasPanelContent>
      </WithEnvConnectionErrorPanel>
    </ConfigurationCanvasLeftPanel>
  );
};
